import { useState, useEffect, useCallback } from 'react';
import {
  ChevronDown, ChevronUp, Save, Loader2, Plus, X, Trash2,
  Eye, Layout, FileText, Globe
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PAGE_CONFIG = {
  recrutement: { label: 'Recrutement', icon: '💼', route: '/recrutement' },
  accueil: { label: 'Accueil', icon: '🏠', route: '/' },
  consulting: { label: 'Consulting', icon: '📊', route: '/consulting' },
  'moana-paye': { label: 'Moana Paye', icon: '💰', route: '/moana-paye' },
  location: { label: 'Location de salles', icon: '🏢', route: '/location-salles' },
};

/* ── Humanize field key ── */
const humanize = (key) =>
  key.replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').replace(/\b\w/g, c => c.toUpperCase());

/* ── String list editor ── */
const StringListEditor = ({ items, onChange, placeholder }) => {
  const [input, setInput] = useState('');
  const add = () => { const v = input.trim(); if (v) { onChange([...items, v]); setInput(''); } };
  return (
    <div>
      <div className="flex gap-2">
        <Input value={input} onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), add())}
          placeholder={placeholder} className="flex-1" />
        <Button type="button" variant="outline" onClick={add} className="border-[#0D9488] text-[#0D9488] flex-shrink-0">
          <Plus className="w-4 h-4" />
        </Button>
      </div>
      {items.length > 0 && (
        <ul className="mt-2 space-y-1">
          {items.map((item, i) => (
            <li key={i} className="flex items-center gap-2 text-sm bg-slate-50 rounded-lg px-3 py-1.5 group">
              <span className="text-xs text-slate-400 w-5">{i + 1}.</span>
              <span className="flex-1 text-slate-700">{typeof item === 'string' ? item : JSON.stringify(item)}</span>
              <button onClick={() => onChange(items.filter((_, idx) => idx !== i))}
                className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-600 transition-opacity">
                <X className="w-3.5 h-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

/* ── Field renderer for a single key-value pair ── */
const FieldEditor = ({ fieldKey, value, onUpdate }) => {
  const label = humanize(fieldKey);

  if (typeof value === 'string') {
    const isLong = value.length > 100;
    return (
      <div>
        <Label className="text-xs text-slate-500 font-semibold">{label}</Label>
        {isLong ? (
          <textarea value={value} onChange={e => onUpdate(e.target.value)} rows={3}
            className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-[#0D9488]" />
        ) : (
          <Input value={value} onChange={e => onUpdate(e.target.value)} className="mt-1" />
        )}
      </div>
    );
  }

  if (typeof value === 'number') {
    return (
      <div>
        <Label className="text-xs text-slate-500 font-semibold">{label}</Label>
        <Input type="number" value={value} onChange={e => onUpdate(Number(e.target.value))} className="mt-1 w-32" />
      </div>
    );
  }

  if (typeof value === 'boolean') {
    return (
      <div className="flex items-center gap-2">
        <input type="checkbox" checked={value} onChange={e => onUpdate(e.target.checked)} className="w-4 h-4 rounded text-[#0D9488]" />
        <Label className="text-xs text-slate-500 font-semibold">{label}</Label>
      </div>
    );
  }

  if (Array.isArray(value) && (value.length === 0 || typeof value[0] === 'string')) {
    return (
      <div>
        <Label className="text-xs text-slate-500 font-semibold">{label} ({value.length})</Label>
        <StringListEditor items={value} onChange={onUpdate} placeholder={`Ajouter ${label.toLowerCase()}`} />
      </div>
    );
  }

  if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object') {
    return <ObjectArrayEditor label={label} items={value} onChange={onUpdate} />;
  }

  if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
    return <NestedObjectEditor label={label} obj={value} onChange={onUpdate} />;
  }

  return null;
};

/* ── Nested object editor (non-recursive: max 1 level deep) ── */
const NestedObjectEditor = ({ label, obj, onChange }) => {
  const update = (key, val) => onChange({ ...obj, [key]: val });
  return (
    <div>
      {label && <Label className="text-xs text-slate-500 font-semibold mb-1 block">{label}</Label>}
      <div className="pl-3 border-l-2 border-slate-100 space-y-3">
        {Object.entries(obj).map(([k, v]) => {
          if (typeof v === 'string') {
            const isLong = v.length > 100;
            return (
              <div key={k}>
                <Label className="text-xs text-slate-400">{humanize(k)}</Label>
                {isLong ? (
                  <textarea value={v} onChange={e => update(k, e.target.value)} rows={2}
                    className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-[#0D9488]" />
                ) : (
                  <Input value={v} onChange={e => update(k, e.target.value)} className="mt-1" />
                )}
              </div>
            );
          }
          if (typeof v === 'boolean') {
            return (
              <div key={k} className="flex items-center gap-2">
                <input type="checkbox" checked={v} onChange={e => update(k, e.target.checked)} className="w-4 h-4" />
                <Label className="text-xs text-slate-400">{humanize(k)}</Label>
              </div>
            );
          }
          if (typeof v === 'number') {
            return (
              <div key={k}>
                <Label className="text-xs text-slate-400">{humanize(k)}</Label>
                <Input type="number" value={v} onChange={e => update(k, Number(e.target.value))} className="mt-1 w-24" />
              </div>
            );
          }
          if (Array.isArray(v) && (v.length === 0 || typeof v[0] === 'string')) {
            return (
              <div key={k}>
                <Label className="text-xs text-slate-400">{humanize(k)} ({v.length})</Label>
                <StringListEditor items={v} onChange={newV => update(k, newV)} placeholder={`Ajouter`} />
              </div>
            );
          }
          return null;
        })}
      </div>
    </div>
  );
};

/* ── Array of objects editor ── */
const ObjectArrayEditor = ({ label, items, onChange }) => {
  const addItem = () => {
    if (items.length === 0) return;
    const tmpl = {};
    Object.keys(items[0]).forEach(k => {
      const sample = items[0][k];
      tmpl[k] = typeof sample === 'string' ? '' : typeof sample === 'boolean' ? false : Array.isArray(sample) ? [] : '';
    });
    onChange([...items, tmpl]);
  };

  const updateItem = (idx, newItem) => {
    const copy = [...items];
    copy[idx] = newItem;
    onChange(copy);
  };

  const removeItem = (idx) => onChange(items.filter((_, i) => i !== idx));

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <Label className="text-xs text-slate-500 font-semibold">{label} ({items.length})</Label>
        <Button type="button" variant="outline" size="sm" onClick={addItem}
          className="text-[#0D9488] border-[#0D9488] text-xs h-7">
          <Plus className="w-3 h-3 mr-1" /> Ajouter
        </Button>
      </div>
      <div className="space-y-2">
        {items.map((item, i) => (
          <div key={i} className="border border-gray-200 rounded-lg p-3 relative group">
            <div className="flex justify-between items-center mb-2">
              <span className="text-xs font-semibold text-slate-400">#{i + 1}</span>
              <button onClick={() => removeItem(i)}
                className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-600 transition-opacity">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="space-y-3">
              {Object.entries(item).map(([k, v]) => {
                if (typeof v === 'string') {
                  const isLong = v.length > 80;
                  return (
                    <div key={k}>
                      <Label className="text-xs text-slate-400">{humanize(k)}</Label>
                      {isLong ? (
                        <textarea value={v} rows={2}
                          onChange={e => updateItem(i, { ...item, [k]: e.target.value })}
                          className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-[#0D9488]" />
                      ) : (
                        <Input value={v}
                          onChange={e => updateItem(i, { ...item, [k]: e.target.value })}
                          className="mt-1" />
                      )}
                    </div>
                  );
                }
                if (typeof v === 'boolean') {
                  return (
                    <div key={k} className="flex items-center gap-2">
                      <input type="checkbox" checked={v}
                        onChange={e => updateItem(i, { ...item, [k]: e.target.checked })}
                        className="w-4 h-4" />
                      <Label className="text-xs text-slate-400">{humanize(k)}</Label>
                    </div>
                  );
                }
                if (typeof v === 'number') {
                  return (
                    <div key={k}>
                      <Label className="text-xs text-slate-400">{humanize(k)}</Label>
                      <Input type="number" value={v}
                        onChange={e => updateItem(i, { ...item, [k]: Number(e.target.value) })}
                        className="mt-1 w-24" />
                    </div>
                  );
                }
                if (Array.isArray(v) && (v.length === 0 || typeof v[0] === 'string')) {
                  return (
                    <div key={k}>
                      <Label className="text-xs text-slate-400">{humanize(k)} ({v.length})</Label>
                      <StringListEditor items={v}
                        onChange={newV => updateItem(i, { ...item, [k]: newV })}
                        placeholder="Ajouter" />
                    </div>
                  );
                }
                return null;
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ── Section content editor (top-level) ── */
const SectionEditor = ({ content, onChange }) => {
  if (!content || typeof content !== 'object') return null;
  const update = (key, val) => onChange({ ...content, [key]: val });

  return (
    <div className="space-y-4">
      {Object.entries(content).map(([key, val]) => (
        <FieldEditor key={key} fieldKey={key} value={val} onUpdate={v => update(key, v)} />
      ))}
    </div>
  );
};

/* ══════════════════════════════════════
   MAIN ADMIN SITE CONTENT TAB
══════════════════════════════════════ */
export const AdminSiteContentTab = ({ token }) => {
  const [activePage, setActivePage] = useState('recrutement');
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedSection, setExpandedSection] = useState(null);
  const [saving, setSaving] = useState(null);
  const [dirty, setDirty] = useState({});

  const headers = { Authorization: `Bearer ${token}` };

  const fetchSections = useCallback(async () => {
    setLoading(true);
    setExpandedSection(null);
    setDirty({});
    try {
      const { data } = await axios.get(`${API}/admin/site-content/${activePage}`, { headers });
      setSections(data);
    } catch {
      toast.error('Erreur chargement du contenu');
      setSections([]);
    } finally {
      setLoading(false);
    }
  }, [token, activePage]);

  useEffect(() => { fetchSections(); }, [fetchSections]);

  const updateSectionContent = (sectionId, newContent) => {
    setSections(prev => prev.map(s =>
      s.section === sectionId ? { ...s, content: newContent } : s
    ));
    setDirty(prev => ({ ...prev, [sectionId]: true }));
  };

  const saveSection = async (sectionId) => {
    const section = sections.find(s => s.section === sectionId);
    if (!section) return;
    setSaving(sectionId);
    try {
      await axios.put(`${API}/admin/site-content/${activePage}/${sectionId}`, { content: section.content }, { headers });
      toast.success(`Section "${section.label}" sauvegardee`);
      setDirty(prev => ({ ...prev, [sectionId]: false }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur de sauvegarde');
    } finally {
      setSaving(null);
    }
  };

  const saveAll = async () => {
    const dirtyKeys = Object.keys(dirty).filter(k => dirty[k]);
    if (dirtyKeys.length === 0) { toast.info('Aucune modification a sauvegarder'); return; }
    for (const key of dirtyKeys) { await saveSection(key); }
  };

  const pageConf = PAGE_CONFIG[activePage] || { label: activePage, icon: '📄', route: '/' };

  return (
    <div data-testid="admin-site-content-tab">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-4 flex-wrap">
          <div>
            <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
              <Layout className="w-5 h-5 text-[#0D9488]" />
              CMS Visuel
            </h2>
            <p className="text-sm text-slate-500 mt-0.5">Editez le contenu de vos pages en temps réel</p>
          </div>
          <Select value={activePage} onValueChange={setActivePage}>
            <SelectTrigger className="w-[220px] border-[#0D9488] text-[#0D9488] font-medium" data-testid="page-selector">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(PAGE_CONFIG).map(([slug, conf]) => (
                <SelectItem key={slug} value={slug}>
                  <span className="flex items-center gap-2">{conf.icon} {conf.label}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => window.open(pageConf.route, '_blank')}
            className="border-[#0D9488] text-[#0D9488]" data-testid="preview-site-btn">
            <Eye className="w-4 h-4 mr-2" /> Voir la page
          </Button>
          <Button onClick={saveAll} disabled={!Object.values(dirty).some(Boolean)}
            className="bg-[#0D9488] hover:bg-[#0F766E] text-white" data-testid="save-all-btn">
            <Save className="w-4 h-4 mr-2" /> Tout sauvegarder
          </Button>
        </div>
      </div>

      {/* Page pills */}
      <div className="flex gap-1.5 mb-6 flex-wrap" data-testid="page-pills">
        {Object.entries(PAGE_CONFIG).map(([slug, conf]) => (
          <button key={slug} onClick={() => setActivePage(slug)}
            className={`px-3.5 py-1.5 rounded-full text-xs font-medium transition-all ${
              activePage === slug
                ? 'bg-[#0D9488] text-white shadow-sm'
                : 'bg-white border border-slate-200 text-slate-500 hover:border-[#0D9488] hover:text-[#0D9488]'
            }`}
            data-testid={`page-pill-${slug}`}>
            {conf.icon} {conf.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
        </div>
      ) : sections.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-dashed border-slate-300">
          <Globe className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">Aucune section editable pour cette page.</p>
          <p className="text-sm text-slate-400 mt-1">Le contenu sera généré au prochain redemarrage du serveur.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {sections.map(section => {
            const isExpanded = expandedSection === section.section;
            const isDirty = dirty[section.section];
            return (
              <div key={section.section}
                className={`bg-white rounded-xl border transition-all ${
                  isExpanded ? 'border-[#0D9488] shadow-md' : isDirty ? 'border-amber-300' : 'border-gray-200'
                }`}
                data-testid={`section-${section.section}`}>
                <div role="button" tabIndex={0}
                  onClick={() => setExpandedSection(isExpanded ? null : section.section)}
                  onKeyDown={e => e.key === 'Enter' && setExpandedSection(isExpanded ? null : section.section)}
                  className="w-full flex items-center justify-between p-4 hover:bg-slate-50/50 transition-colors text-left cursor-pointer"
                  data-testid={`section-toggle-${section.section}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-[#0D9488]/10 flex items-center justify-center">
                      <FileText className="w-5 h-5 text-[#0D9488]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-800 text-sm">{section.label}</h3>
                      <p className="text-xs text-slate-400">Section : {section.section}</p>
                    </div>
                    {isDirty && (
                      <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">Modifie</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {isDirty && (
                      <Button size="sm"
                        onClick={e => { e.stopPropagation(); saveSection(section.section); }}
                        disabled={saving === section.section}
                        className="bg-[#0D9488] hover:bg-[#0F766E] text-white text-xs h-8"
                        data-testid={`save-${section.section}`}>
                        {saving === section.section ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
                        Sauvegarder
                      </Button>
                    )}
                    {isExpanded ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
                  </div>
                </div>
                {isExpanded && (
                  <div className="border-t border-gray-100 p-5" data-testid={`editor-${section.section}`}>
                    <SectionEditor
                      content={section.content || {}}
                      onChange={newContent => updateSectionContent(section.section, newContent)}
                    />
                    <div className="flex justify-end mt-4 pt-3 border-t border-gray-100">
                      <Button onClick={() => saveSection(section.section)}
                        disabled={saving === section.section || !isDirty}
                        className="bg-[#0D9488] hover:bg-[#0F766E] text-white"
                        data-testid={`save-bottom-${section.section}`}>
                        {saving === section.section ? (
                          <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sauvegarde...</>
                        ) : (
                          <><Save className="w-4 h-4 mr-2" /> Sauvegarder cette section</>
                        )}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminSiteContentTab;
